import { httpsCallable } from 'firebase/functions'
import { firebaseFunctions } from './firebase'
import type { StripboardDelta, StripboardDeltaResult } from '../types/stripboard'
import {
  type ChecklistBlockerCode,
  BLOCKER_MESSAGES,
} from '../types/checklistBlockerCodes'

export interface BlockerErrorDetail {
  code: ChecklistBlockerCode
  message: string
}

export class ChecklistBlockerError extends Error {
  readonly code: ChecklistBlockerCode
  readonly blockers: BlockerErrorDetail[]

  constructor(
    message: string,
    payload: {
      code: ChecklistBlockerCode
      blockers?: BlockerErrorDetail[]
    },
  ) {
    super(message)
    this.name = 'ChecklistBlockerError'
    this.code = payload.code
    this.blockers = payload.blockers && payload.blockers.length > 0
      ? payload.blockers
      : [{ code: payload.code, message }]
  }
}

const getProcessDelta = () => httpsCallable<StripboardDelta, StripboardDeltaResult>(firebaseFunctions, 'processStripboardDelta')

export const applyStripboardDelta = async (delta: StripboardDelta): Promise<StripboardDeltaResult> => {
  try {
    const processDelta = getProcessDelta()
    const response = await processDelta(delta)
    return response.data
  } catch (error: unknown) {
    const err = error as {
      code?: string
      message?: string
      details?: {
        code?: ChecklistBlockerCode
        blockers?: BlockerErrorDetail[]
      }
    }
    const isFailedPrecondition =
      err?.code === 'functions/failed-precondition' || err?.code === 'failed-precondition'
    const blockerCode = err?.details?.code
    if (isFailedPrecondition && blockerCode) {
      const blockers = Array.isArray(err.details?.blockers) && err.details.blockers.length > 0
        ? err.details.blockers
        : [{ code: blockerCode, message: err.message || BLOCKER_MESSAGES[blockerCode] || 'Action blocked' }]
      throw new ChecklistBlockerError(err.message || BLOCKER_MESSAGES[blockerCode] || 'Action blocked', {
        code: blockerCode,
        blockers,
      })
    }
    throw error
  }
}

export const createDeltaOperationId = (prefix: string, recordId: string) => {
  const random = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`
  return `${prefix}:${recordId}:${random}`
}
